// =====================================================================
// /admin/validation-ts — Validation des feuilles de temps (Sub-faza 3.2)
// =====================================================================
// D-002 REV: Aprobare per TimesheetEntry (1 linie = 1 unitate),
// match cu Odoo legacy UX (NU per TS day).
//
// Functionalitati:
// - Listă flat: 1 row per entry SUBMITTED unde admin e principal/backup
// - Filter bar: opérateur, projet, plage de dates, rôle
// - Bulk select + bar fix-bottom "X sélectionnée(s) — Approuver tous"
// - Modal Rejeter cu raison (3-500 caractères)
// - Banner sus dacă cel puțin 1 entry are primaryAbsent (Q10)
// - Pills color-coded: principal (bleu) / backup (gris) / alerte ⚠ (jaune)
// - Buton "Exporter CSV" — generare client-side cu BOM UTF-8 pentru Excel
// - Reload list după fiecare action (fresh state, evită race cu alți admin)
//
// Backend endpoints (api.js → timesheetsValidation):
//   GET  /api/timesheets/admin/pending
//   POST /api/timesheets/admin/entries/:id/approve
//   POST /api/timesheets/admin/entries/:id/reject  { reason }
//
// Permission gating: timesheets.approve.group
// =====================================================================

import { useEffect, useState, useMemo } from 'react';
import { timesheetsValidation } from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import PeriodLockBanner from '../../components/PeriodLockBanner.jsx';
import { neutraliserFormule } from '../../lib/csv.js';

// ---------------------------------------------------------------------
// Avatar helpers (copied from Users.jsx for visual consistency)
// ---------------------------------------------------------------------

const AVATAR_GRADIENTS = [
  ['#6366f1', '#8b5cf6'],
  ['#ec4899', '#f43f5e'],
  ['#06b6d4', '#3b82f6'],
  ['#10b981', '#14b8a6'],
  ['#f59e0b', '#ef4444'],
  ['#8b5cf6', '#d946ef'],
  ['#0ea5e9', '#22d3ee'],
];

function getInitials(name) {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getGradient(id) {
  let hash = 0;
  for (let i = 0; i < (id || '').length; i++) hash = (hash * 31 + id.charCodeAt(i)) | 0;
  return AVATAR_GRADIENTS[Math.abs(hash) % AVATAR_GRADIENTS.length];
}

// ---------------------------------------------------------------------
// Format helpers
// ---------------------------------------------------------------------

function fmtDuration(min) {
  if (min == null) return '—';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h${m.toString().padStart(2, '0')}`;
}

function fmtDate(ymd) {
  if (!ymd) return '?';
  const [y, m, d] = ymd.split('-');
  return `${d}/${m}/${y}`;
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-BE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return '?';
  }
}

// ---------------------------------------------------------------------
// CSV export (client-side, no library)
// BOM UTF-8 pentru deschidere corectă în Excel.
// ---------------------------------------------------------------------

function escapeCSV(val) {
  if (val == null) return '';
  const s = neutraliserFormule(String(val));   // formule (= + - @) d'une description saisie : texte (lib/csv.js)
  if (s.includes('"') || s.includes(',') || s.includes('\n') || s.includes('\r') || s.includes(';')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function exportToCSV(entries) {
  const headers = [
    'Date', 'Opérateur', 'Username', 'Projet', 'Tâche',
    'Description', 'Soumis le', 'Durée (min)', 'Durée (h)', 'Rôle', 'Alerte',
  ];
  const rows = entries.map(e => [
    fmtDate(e.date),
    e.userName,
    e.username,
    e.projectName,
    e.taskName || '',
    e.description || '',
    fmtDateTime(e.submittedAt),
    e.durationMin,
    fmtDuration(e.durationMin),
    e.isBackup ? 'Backup' : 'Principal',
    e.primaryAbsent ? 'OUI' : '',
  ].map(escapeCSV).join(','));

  const csv = '\uFEFF' + headers.join(',') + '\n' + rows.join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const today = new Date().toISOString().slice(0, 10);
  link.download = `validation-ts-${today}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------

export default function AdminValidationTSPage({ embedded = false }) {
  const { can } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtres
  const [filterUser, setFilterUser] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterRole, setFilterRole] = useState('all'); // all | principal | backup | alert

  // Sélection bulk
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  // Modal rejet
  // null | { entry, reason, submitting, error }
  const [rejectModal, setRejectModal] = useState(null);

  // Toast
  // null | { type: 'success'|'error'|'info', message, id }
  const [toast, setToast] = useState(null);

  const canApprove = can('timesheets.approve.group');

  // -------------------------------------------------------------------
  // Toast handler with auto-hide (4s)
  // -------------------------------------------------------------------

  const showToast = (type, message) => {
    setToast({ type, message, id: Date.now() });
  };

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // -------------------------------------------------------------------
  // Load entries
  // -------------------------------------------------------------------

  const loadEntries = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await timesheetsValidation.listPending();
      const arr = Array.isArray(data) ? data : [];
      setEntries(arr);
      // Curățăm selecții care nu mai există în noul snapshot
      setSelectedIds(prev => prev.filter(id => arr.some(e => e.entryId === id)));
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur de chargement');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canApprove) loadEntries();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canApprove]);

  // -------------------------------------------------------------------
  // Derived state
  // -------------------------------------------------------------------

  const filteredEntries = useMemo(() => {
    return entries.filter(e => {
      if (filterUser) {
        const q = filterUser.toLowerCase();
        const matchUser =
          (e.userName || '').toLowerCase().includes(q) ||
          (e.username || '').toLowerCase().includes(q);
        if (!matchUser) return false;
      }
      if (filterProject && e.projectId !== filterProject) return false;
      if (filterDateFrom && e.date < filterDateFrom) return false;
      if (filterDateTo && e.date > filterDateTo) return false;
      if (filterRole === 'principal' && e.isBackup) return false;
      if (filterRole === 'backup' && !e.isBackup) return false;
      if (filterRole === 'alert' && !e.primaryAbsent) return false;
      return true;
    });
  }, [entries, filterUser, filterProject, filterDateFrom, filterDateTo, filterRole]);

  const projectsInList = useMemo(() => {
    const map = new Map();
    for (const e of entries) {
      if (!map.has(e.projectId)) map.set(e.projectId, e.projectName);
    }
    return Array.from(map.entries())
      .sort((a, b) => (a[1] || '').localeCompare(b[1] || '', 'fr'))
      .map(([id, name]) => ({ id, name }));
  }, [entries]);

  const hasBackupAlerts = useMemo(
    () => entries.some(e => e.primaryAbsent),
    [entries]
  );

  const totalDurationMin = useMemo(
    () => filteredEntries.reduce((s, e) => s + (e.durationMin || 0), 0),
    [filteredEntries]
  );

  const filteredIds = useMemo(
    () => filteredEntries.map(e => e.entryId),
    [filteredEntries]
  );

  const allFilteredSelected =
    filteredIds.length > 0 && filteredIds.every(id => selectedIds.includes(id));

  // -------------------------------------------------------------------
  // Bulk select handlers
  // -------------------------------------------------------------------

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds(prev => prev.filter(id => !filteredIds.includes(id)));
    } else {
      setSelectedIds(prev => Array.from(new Set([...prev, ...filteredIds])));
    }
  };

  // -------------------------------------------------------------------
  // Approve / Reject handlers
  // -------------------------------------------------------------------

  const handleApproveOne = async (entry) => {
    const ok = window.confirm(
      `Approuver l'entrée du ${fmtDate(entry.date)} (${entry.userName} — ${entry.projectName}) ?`
    );
    if (!ok) return;
    try {
      await timesheetsValidation.approve(entry.entryId);
      showToast('success', `Entrée approuvée: ${entry.userName} — ${fmtDate(entry.date)}`);
      await loadEntries();
    } catch (err) {
      const code = err.response?.data?.error;
      const msg = err.response?.data?.message || "Erreur lors de l'approbation";
      if (code === 'ALREADY_APPROVED' || code === 'ALREADY_REJECTED' || code === 'ENTRY_NOT_FOUND') {
        showToast('info', `${msg} — actualisation de la liste`);
        await loadEntries();
      } else {
        showToast('error', msg);
      }
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Approuver ${selectedIds.length} entrée(s) ?`)) return;
    setBulkSubmitting(true);
    let successCount = 0;
    let skippedCount = 0;
    let lastError = null;

    // Sequential pentru predictibilitate + evită race la backend
    for (const id of selectedIds) {
      try {
        await timesheetsValidation.approve(id);
        successCount++;
      } catch (err) {
        const code = err.response?.data?.error;
        if (code === 'ALREADY_APPROVED' || code === 'ALREADY_REJECTED' || code === 'ENTRY_NOT_FOUND') {
          // Race: alt admin a tratat-o între timp; sărim peste, continuăm
          skippedCount++;
          continue;
        }
        lastError = err.response?.data?.message || 'Erreur';
        break;
      }
    }

    setBulkSubmitting(false);
    setSelectedIds([]);
    await loadEntries();

    if (lastError) {
      showToast('error', `${successCount} approuvée(s), ${skippedCount} ignorée(s), arrêt: ${lastError}`);
    } else if (skippedCount > 0) {
      showToast('info', `${successCount} approuvée(s), ${skippedCount} ignorée(s) (déjà traitées)`);
    } else {
      showToast('success', `${successCount} entrée(s) approuvée(s)`);
    }
  };

  const openRejectModal = (entry) => {
    setRejectModal({ entry, reason: '', submitting: false, error: null });
  };

  const closeRejectModal = () => {
    if (rejectModal?.submitting) return;
    setRejectModal(null);
  };

  const submitReject = async (e) => {
    e.preventDefault();
    if (!rejectModal) return;
    const { entry, reason } = rejectModal;
    const cleanReason = (reason || '').trim();

    if (cleanReason.length < 3) {
      setRejectModal(prev => prev ? { ...prev, error: 'Motif requis (3 caractères minimum)' } : null);
      return;
    }
    if (cleanReason.length > 500) {
      setRejectModal(prev => prev ? { ...prev, error: 'Motif trop long (500 caractères maximum)' } : null);
      return;
    }

    setRejectModal(prev => prev ? { ...prev, submitting: true, error: null } : null);
    try {
      await timesheetsValidation.reject(entry.entryId, cleanReason);
      setRejectModal(null);
      showToast('success', `Entrée rejetée: ${entry.userName} — ${fmtDate(entry.date)}`);
      await loadEntries();
    } catch (err) {
      const code = err.response?.data?.error;
      const msg = err.response?.data?.message || 'Erreur lors du rejet';
      if (code === 'ALREADY_APPROVED' || code === 'ALREADY_REJECTED' || code === 'ENTRY_NOT_FOUND') {
        setRejectModal(null);
        showToast('info', `${msg} — actualisation de la liste`);
        await loadEntries();
      } else {
        setRejectModal(prev => prev ? { ...prev, submitting: false, error: msg } : null);
      }
    }
  };

  // -------------------------------------------------------------------
  // Filters reset
  // -------------------------------------------------------------------

  const resetFilters = () => {
    setFilterUser('');
    setFilterProject('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setFilterRole('all');
  };

  const hasActiveFilters =
    filterUser || filterProject || filterDateFrom || filterDateTo || filterRole !== 'all';

  // -------------------------------------------------------------------
  // Permission gate
  // -------------------------------------------------------------------

  if (!canApprove) {
    return (
      <div className="admin-validation-ts">
        {!embedded && (
          <div className="page-header">
            <h1>Validation des feuilles de temps</h1>
          </div>
        )}
        <div className="alert alert-error">
          Vous n'avez pas l'autorisation de valider les feuilles de temps.
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------

  return (
    <div className="admin-validation-ts">
      {/* Toast */}
      {toast && (
        <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />
      )}

      {/* Page header */}
      {!embedded && (
        <div className="page-header">
          <h1>
            Validation des feuilles de temps{' '}
            <span className="count-badge">{filteredEntries.length}</span>
          </h1>
        </div>
      )}

      {/* Period lock banner (Faza 3.4 D-007) */}
      <PeriodLockBanner />

      {/* Backup alert banner (Q10) */}
      {hasBackupAlerts && (
        <div style={{
          background: '#fef3c7',
          border: '1px solid #fcd34d',
          color: '#92400e',
          padding: '12px 16px',
          borderRadius: 6,
          marginBottom: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}>
          <span style={{ fontSize: 18 }}>⚠️</span>
          <span>
            <strong>Attention:</strong> certaines entrées sont sous votre approbation en tant que{' '}
            <strong>backup</strong> et le responsable principal est absent aujourd'hui.
            Filtrez par "⚠ Backup avec alerte" pour les voir.
          </span>
        </div>
      )}

      {/* Filter bar */}
      <div className="toolbar" style={{ flexWrap: 'wrap', gap: 8 }}>
        <input
          type="text"
          placeholder="🔍 Opérateur (nom ou username)..."
          value={filterUser}
          onChange={e => setFilterUser(e.target.value)}
          className="search-input"
          style={{ minWidth: 220 }}
        />
        <select value={filterProject} onChange={e => setFilterProject(e.target.value)}>
          <option value="">Tous les projets</option>
          {projectsInList.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <input
          type="date"
          value={filterDateFrom}
          onChange={e => setFilterDateFrom(e.target.value)}
          title="Date de début"
        />
        <input
          type="date"
          value={filterDateTo}
          onChange={e => setFilterDateTo(e.target.value)}
          title="Date de fin"
        />
        <select value={filterRole} onChange={e => setFilterRole(e.target.value)}>
          <option value="all">Tous les rôles</option>
          <option value="principal">Principal uniquement</option>
          <option value="backup">Backup uniquement</option>
          <option value="alert">⚠ Backup avec alerte</option>
        </select>
        {hasActiveFilters && (
          <button type="button" className="btn" onClick={resetFilters}
                  title="Réinitialiser les filtres">
            ✕ Réinitialiser
          </button>
        )}
        <div style={{ flex: 1 }} />
        <button
          type="button"
          className="btn"
          onClick={() => exportToCSV(filteredEntries)}
          disabled={filteredEntries.length === 0}
          title="Exporter la liste filtrée en CSV (UTF-8 BOM, compatible Excel)"
        >
          📥 Exporter CSV ({filteredEntries.length})
        </button>
        <button
          type="button"
          className="btn"
          onClick={async () => {
            const now = new Date();
            const y = now.getFullYear();
            const m = String(now.getMonth() + 1).padStart(2, '0');
            const lastDay = new Date(y, now.getMonth() + 1, 0).getDate();
            const from = window.prompt('Date debut (YYYY-MM-DD):', `${y}-${m}-01`);
            if (!from) return;
            const to = window.prompt('Date fin (YYYY-MM-DD):', `${y}-${m}-${String(lastDay).padStart(2,'0')}`);
            if (!to) return;
            try {
              const resp = await fetch(`/api/admin/export/global?from=${from}&to=${to}`, { credentials: 'include' });
              if (!resp.ok) throw new Error('HTTP ' + resp.status);
              const statsHeader = resp.headers.get('X-Export-Stats');
              const blob = await resp.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `brain-elog-export-${from}_${to}.xlsx`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(url);
              if (statsHeader) {
                const stats = JSON.parse(statsHeader);
                alert('Export genere:\n- ' + stats.attendances + ' presences\n- ' + stats.timesheetEntries + ' TS\n- ' + stats.leaves + ' conges\n- ' + stats.overtimes + ' heures sup.\n- ' + stats.balances + ' soldes\n- ' + stats.users + ' utilisateurs\n- ' + stats.auditLogs + ' logs');
              }
            } catch (err) {
              alert('Erreur export: ' + err.message);
            }
          }}
          title="Exporter TOUT (presences, TS, conges, overtime, soldes, users, audit) en Excel multi-onglets"
          style={{ marginLeft: 8, background: '#15803d', color: 'white', borderColor: '#15803d' }}
        >
          Exporter tout (XLSX)
        </button>
      </div>

      {/* Stats bar */}
      {!loading && filteredEntries.length > 0 && (
        <div style={{
          fontSize: 13, color: '#6b7280', margin: '8px 4px',
          display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap',
        }}>
          <span><strong>{filteredEntries.length}</strong> entrée(s) affichée(s)</span>
          <span>Total: <strong>{fmtDuration(totalDurationMin)}</strong></span>
          {entries.length !== filteredEntries.length && (
            <span style={{ color: '#9ca3af' }}>
              ({entries.length - filteredEntries.length} masquée(s) par filtres)
            </span>
          )}
        </div>
      )}

      {/* Error */}
      {error && <div className="alert alert-error">{error}</div>}

      {/* Table */}
      <div className="table-wrapper" style={{ paddingBottom: selectedIds.length > 0 ? 80 : 0 }}>
        {loading ? (
          <div className="loading">Chargement...</div>
        ) : filteredEntries.length === 0 ? (
          <div className="empty">
            {entries.length === 0
              ? 'Aucune entrée à valider 🎉'
              : 'Aucune entrée ne correspond aux filtres'}
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ width: 32 }}>
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    onChange={toggleSelectAll}
                    title={allFilteredSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
                  />
                </th>
                <th>Date</th>
                <th>Opérateur</th>
                <th>Projet</th>
                <th>Tâche</th>
                <th>Description</th>
                <th>Soumis le</th>
                <th>Temps</th>
                <th>Rôle</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.map(e => {
                const [c1, c2] = getGradient(e.userId);
                const isSelected = selectedIds.includes(e.entryId);
                return (
                  <tr
                    key={e.entryId}
                    className={isSelected ? 'row-selected' : ''}
                    style={isSelected ? { background: '#eff6ff' } : undefined}
                  >
                    <td>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(e.entryId)}
                      />
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(e.date)}</td>
                    <td>
                      <div className="name-cell">
                        <span
                          className="avatar"
                          style={{ background: `linear-gradient(135deg, ${c1}, ${c2})` }}
                        >
                          {getInitials(e.userName)}
                        </span>
                        <div>
                          <div className="name">{e.userName}</div>
                          <div className="username">{e.username}</div>
                        </div>
                      </div>
                    </td>
                    <td>{e.projectName}</td>
                    <td>{e.taskName || <span className="text-muted">—</span>}</td>
                    <td style={{ maxWidth: 240, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                      {e.description || <span className="text-muted">—</span>}
                    </td>
                    <td style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>
                      {fmtDateTime(e.submittedAt)}
                    </td>
                    <td><strong>{fmtDuration(e.durationMin)}</strong></td>
                    <td>
                      <RolePill isBackup={e.isBackup} primaryAbsent={e.primaryAbsent} />
                    </td>
                    <td className="actions" style={{ whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        className="btn-icon"
                        onClick={() => handleApproveOne(e)}
                        title="Approuver"
                        style={{ color: '#059669', fontSize: 18 }}
                      >
                        ✓
                      </button>
                      <button
                        type="button"
                        className="btn-icon"
                        onClick={() => openRejectModal(e)}
                        title="Rejeter"
                        style={{ color: '#dc2626', fontSize: 18 }}
                      >
                        ✗
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Bulk action bar — fix-bottom */}
      {selectedIds.length > 0 && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'white',
          borderTop: '2px solid #3b82f6',
          padding: '12px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          boxShadow: '0 -4px 12px rgba(0,0,0,0.1)',
          zIndex: 40,
        }}>
          <strong>{selectedIds.length} sélectionnée(s)</strong>
          <button type="button" className="btn" onClick={() => setSelectedIds([])}>
            Tout désélectionner
          </button>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleBulkApprove}
            disabled={bulkSubmitting}
            style={{ background: '#059669', borderColor: '#059669' }}
          >
            {bulkSubmitting
              ? 'Traitement...'
              : `✓ Approuver ${selectedIds.length} entrée(s)`}
          </button>
        </div>
      )}

      {/* Reject modal */}
      {rejectModal && (
        <RejectModal
          state={rejectModal}
          onChange={(patch) =>
            setRejectModal(prev => prev ? { ...prev, ...patch } : null)
          }
          onClose={closeRejectModal}
          onSubmit={submitReject}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Role Pill
// ---------------------------------------------------------------------

function RolePill({ isBackup, primaryAbsent }) {
  if (primaryAbsent) {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600,
        background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d',
        whiteSpace: 'nowrap',
      }} title="Backup approver — le responsable principal est absent aujourd'hui">
        ⚠ Backup
      </span>
    );
  }
  if (isBackup) {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center',
        padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600,
        background: '#f3f4f6', color: '#4b5563', border: '1px solid #d1d5db',
        whiteSpace: 'nowrap',
      }}>
        Backup
      </span>
    );
  }
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 600,
      background: '#dbeafe', color: '#1e40af', border: '1px solid #93c5fd',
      whiteSpace: 'nowrap',
    }}>
      Principal
    </span>
  );
}

// ---------------------------------------------------------------------
// Reject Modal (inline subcomponent, pattern Users.jsx UserModal)
// ---------------------------------------------------------------------

function RejectModal({ state, onChange, onClose, onSubmit }) {
  const { entry, reason, submitting, error } = state;
  const len = (reason || '').trim().length;
  const canSubmit = len >= 3 && len <= 500 && !submitting;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Rejeter l'entrée</h2>
          <button type="button" className="btn-close" onClick={onClose} disabled={submitting}>
            ×
          </button>
        </div>
        <form onSubmit={onSubmit}>
          <div className="modal-body">
            {error && <div className="alert alert-error">{error}</div>}

            <div style={{
              background: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: 6,
              padding: 12,
              marginBottom: 16,
              fontSize: 13,
              lineHeight: 1.6,
            }}>
              <div><strong>Opérateur:</strong> {entry.userName} ({entry.username})</div>
              <div><strong>Date:</strong> {fmtDate(entry.date)}</div>
              <div><strong>Projet:</strong> {entry.projectName}</div>
              <div><strong>Tâche:</strong> {entry.taskName || '—'}</div>
              <div><strong>Durée:</strong> {fmtDuration(entry.durationMin)}</div>
              {entry.description && (
                <div><strong>Description:</strong> {entry.description}</div>
              )}
            </div>

            <div className="form-row">
              <label>
                Motif du rejet <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <textarea
                value={reason}
                onChange={e => onChange({ reason: e.target.value, error: null })}
                rows={4}
                maxLength={500}
                required
                autoFocus
                placeholder="Expliquez pourquoi cette entrée est rejetée (minimum 3 caractères)..."
                style={{
                  width: '100%',
                  padding: 8,
                  border: '1px solid #d1d5db',
                  borderRadius: 4,
                  fontFamily: 'inherit',
                  fontSize: 14,
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
              />
              <small style={{
                display: 'block',
                marginTop: 4,
                color: len < 3 ? '#dc2626' : '#6b7280',
                fontSize: 12,
              }}>
                {len} / 500 caractères {len < 3 && '(minimum 3)'}
              </small>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn" onClick={onClose} disabled={submitting}>
              Annuler
            </button>
            <button
              type="submit"
              className="btn btn-danger"
              disabled={!canSubmit}
              style={{ background: '#dc2626', borderColor: '#dc2626', color: 'white' }}
            >
              {submitting ? 'Rejet en cours...' : 'Rejeter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Toast (auto-hide in parent, fixed top-right)
// ---------------------------------------------------------------------

function Toast({ type, message, onClose }) {
  const colors = {
    success: { bg: '#10b981', border: '#059669' },
    error:   { bg: '#ef4444', border: '#dc2626' },
    info:    { bg: '#3b82f6', border: '#2563eb' },
  };
  const c = colors[type] || colors.info;
  return (
    <div style={{
      position: 'fixed',
      top: 16,
      right: 16,
      background: c.bg,
      color: 'white',
      padding: '12px 16px',
      borderRadius: 6,
      borderLeft: `4px solid ${c.border}`,
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      zIndex: 100,
      maxWidth: 480,
      display: 'flex',
      alignItems: 'center',
      gap: 12,
    }}>
      <span style={{ flex: 1, fontSize: 14 }}>{message}</span>
      <button
        type="button"
        onClick={onClose}
        style={{
          background: 'none', border: 'none', color: 'white',
          fontSize: 18, cursor: 'pointer', padding: 0, lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  );
}
